<template>
    <div class="mt-8">
        <div class="font-semibold mb-2">{{ $t("committedInvestments") }}</div>
        <div v-if="!items.length" class="text-sm">
            {{ $t("noCommittedInvestments") }}
        </div>
        <div v-if="items.length">
            <div class="lg:hidden grid md:grid-cols-2 gap-3">
                <k-panel v-for="item in items" :key="item.uuid" class="bg-white px-2 py-2 border">
                    <div class="flex flex-row mb-4 pb-2 border-b items-center">
                        <div v-if="item.companyLogo" class="mr-2">
                            <img class="object-scale-down" style="height: 48px;"
                                 :src="`/api/guest/download/${item.companyLogo}`">
                        </div>
                        <div class="flex">
                            <div class="font-semibold text-sm">{{ item.companyName }}</div>
                        </div>
                    </div>
                    <k-panel-row>
                        <div slot="left">{{ $t("outstandingPrincipal") }}</div>
                        <div slot="right" class="text-right">
                            {{ item.principalSubscribed | currencyFormatWithoutDecimals(item.currencySymbol) }}
                            <div v-if="item.principalSubscribedByAutoInvest" class="text-xs text-gray-600">
                                Auto: {{ item.principalSubscribedByAutoInvest | currencyFormatWithoutDecimals(item.currencySymbol) }}
                            </div>
                        </div>
                    </k-panel-row>
                    <k-panel-row>
                        <div slot="left">{{ $t("interestRate") }}</div>
                        <div slot="right">{{ item.interestRate | numeralFormat('0.00') }}%</div>
                    </k-panel-row>
                    <k-panel-row>
                        <div slot="left">{{ $t("maturityDate") }}</div>
                        <div slot="right">{{ item.maturityDate | dateFormatShort}}</div>
                    </k-panel-row>
                    <k-panel-row>
                        <div slot="left">{{ $t("nextPaymentDate") }}</div>
                        <div slot="right">{{ item.nextPaymentDateOfSubscribed | dateFormatShort}}</div>
                    </k-panel-row>
                    <k-panel-row>
                        <div slot="left">{{ $t("nextPaymentAmount") }}</div>
                        <div slot="right">{{ item.nextPaymentAmountOfSubscribed | currencyFormatWithoutDecimals(item.currencySymbol) }}
                        </div>
                    </k-panel-row>
                    <k-panel-row>
                        <div slot="left">
                            <a-button type="primary" ghost @click.native.prevent="view(item.uuid)">
                                {{ $t("view") }}
                            </a-button>
                        </div>
                        <div slot="right">
                            <a-popconfirm
                                title="Cancel committed investment?"
                                ok-text="Yes"
                                cancel-text="No"
                                @confirm="unsubscribe(item.uuid)"
                            >
                                <a-button type="danger" ghost>
                                    {{ $t("cancel") }}
                                </a-button>
                            </a-popconfirm>
                        </div>
                    </k-panel-row>
                </k-panel>
            </div>
            <div class="hidden lg:block">
                <table class="table-auto bg-white w-full">
                    <thead>
                    <tr class="text-sm bg-gray-100">
                        <th class="border px-4 py-2" style="min-width: 400px; max-width: 400px; width: 400px;">
                            {{ $t("company") }}
                        </th>
                        <th class="border px-4 py-2 text-center"
                            style="min-width: 100px; max-width: 100px; width: 100px;">{{ $t("kildeRating") }}
                        </th>
                        <th class="border px-4 py-2 text-center">{{ $t("outstandingPrincipal") }}</th>
                        <th class="border px-4 py-2 text-center">{{ $t("interestRate") }}</th>
                        <th class="border px-4 py-2 text-center">{{ $t("maturityDate") }}</th>
                        <th class="border px-4 py-2 text-center">{{ $t("nextPayment") }}</th>
                        <th class="border px-4 py-2 text-center"></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in items" :key="item.uuid" class="hover:bg-gray-100">
                        <td class="border px-4 py-4">
                            <div class="flex flex-row items-center">
                                <div v-if="item.companyLogo" class="mr-2">
                                    <img class="object-scale-down" style="width: 100px;"
                                         :src="`/api/guest/download/${item.companyLogo}`">
                                </div>
                                <div class="ml-2">
                                    <div class="font-semibold text-sm">{{ item.companyName }}</div>
                                    <div class="">
                                            <span v-for="(product, index) in item.productTypes"
                                                  class="text-xs text-gray-700">
                                                <span
                                                    v-if="index > 0">&nbsp;&bull;&nbsp;</span><span>{{ product }}</span>
                                            </span>
                                    </div>
                                    <div class="flex flex-row">
                                        <div class="mr-2 mt-1">
                                            <k-country-flags slot="right"
                                                             :countries="item.countries"></k-country-flags>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </td>
                        <td class="border px-4 py-4  whitespace-no-wrap text-center">
                            {{ item.creditRating }}
                        </td>
                        <td class="border px-4 py-4 whitespace-no-wrap text-right">
                            {{ item.principalSubscribed | currencyFormatWithoutDecimals(item.currencySymbol) }}
                            <div class=" text-xs" v-if="item.principalSubscribedByAutoInvest">Auto:
                                {{
                                    item.principalSubscribedByAutoInvest | currencyFormatWithoutDecimals(item.currencySymbol)
                                }}
                            </div>
                        </td>
                        <td class="border px-4 py-4 whitespace-no-wrap text-right">
                            {{ item.interestRate | numeralFormat('0.00') }}%
                        </td>
                        <td class="border px-4 py-4 whitespace-no-wrap text-right">
                            {{ item.maturityDate | dateFormatShort }}
                        </td>
                        <td class="border px-4 py-4 whitespace-no-wrap text-right">
                            <div class="text-sm">
                                <div><span class="text-gray-700">{{ $t("date") }}:</span>
                                    {{ item.nextPaymentDateOfSubscribed | dateFormatShort }}
                                </div>
                                <div><span class="text-gray-700">{{ $t("amount") }}:</span>
                                    {{
                                        item.nextPaymentAmountOfSubscribed | currencyFormatWithoutDecimals(item.currencySymbol)
                                    }}
                                </div>
                            </div>
                        </td>
                        <td class="border px-4 py-4 whitespace-no-wrap text-center">
                            <router-link :to="`/tranche-invest/${item.uuid}`">
                                <a-button type="primary" ghost class="mr-2">
                                    {{ $t("view") }}
                                </a-button>
                            </router-link>
                            <a-popconfirm
                                title="Cancel committed investment?"
                                ok-text="Yes"
                                cancel-text="No"
                                @confirm="unsubscribe(item.uuid)"
                            >
                                <a-button type="danger" ghost>
                                    {{ $t("cancel") }}
                                </a-button>
                            </a-popconfirm>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        items: {
            type: Array,
            required: true
        }
    },
    methods: {
        async unsubscribe(trancheUuid) {
            this.$emit('unsubscribe', trancheUuid);
        },
        async view(trancheUuid) {
            this.$emit('view', trancheUuid);
        }
    }
};
</script>


